import React from 'react'

import { Formik, Form } from 'formik'
import { useQueryClient } from '@tanstack/react-query'

import { Stack, Typography, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { AddCard } from '@mui/icons-material'

import { useAuth } from '@/hooks/useAuth'
import {
  CardFormFields,
  cardFormInitialValues,
  cardFormValidationSchema,
  useCardAddFormSubmit,
  useMissingProfile,
} from '@/components/CardAddForm'

import { ChargingStepProps, StartChargingStep } from '../hooks/useStartCharging/types'
import { ChargingStepActions } from '../components/ChargingStepActions'

export const AddCardStep: React.FC<ChargingStepProps> = ({ startChargingMethods }) => {
  const { goToStep, selectCard, state, creditCards } = startChargingMethods
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const replacesCardId = state.reregisterCardId ?? undefined
  const replacedCard = creditCards?.results?.find((card) => card.id === replacesCardId)
  const title = replacesCardId ? 'Recadastrar cartão' : 'Adicionar cartão'

  const goToPaymentMethodStep = () => {
    goToStep(StartChargingStep.PAYMENT_METHOD)
  }

  const handleSuccess = (cardId?: number) => {
    queryClient.invalidateQueries({ queryKey: ['payment-credit-cards'] })
    if (cardId) {
      selectCard(cardId)
    }
    goToPaymentMethodStep()
  }

  const handleSubmit = useCardAddFormSubmit(handleSuccess, replacesCardId)
  const missingProfile = useMissingProfile()

  return (
    <Formik initialValues={cardFormInitialValues} validationSchema={cardFormValidationSchema} onSubmit={handleSubmit}>
      {({ submitForm, isSubmitting }) => (
        <>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AddCard color="primary" />
              <Typography variant="h6" fontWeight="bold">
                {title}
              </Typography>
            </Stack>
            {replacedCard && (
              <Typography variant="body2" color="text.secondary">
                Substitui o cartão final {replacedCard.card_mask.slice(-4)}.
              </Typography>
            )}
          </DialogTitle>

          <DialogContent>
            <Stack spacing={3} sx={{ paddingTop: 1 }}>
              <Form>
                <CardFormFields />
              </Form>
            </Stack>
          </DialogContent>

          <DialogActions>
            <ChargingStepActions sx={{ width: '100%' }}>
              <ChargingStepActions.ForwardButton
                onClick={submitForm}
                loading={isSubmitting}
                disabled={isSubmitting || !user?.phone || missingProfile.length > 0}
              >
                {title}
              </ChargingStepActions.ForwardButton>
              <ChargingStepActions.BackwardButton onClick={goToPaymentMethodStep} />
            </ChargingStepActions>
          </DialogActions>
        </>
      )}
    </Formik>
  )
}
